import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatApiError } from '../utils/formatters';
import axios from 'axios';
import { FiDatabase, FiPlus, FiPackage, FiChevronDown, FiChevronRight, FiDownload, FiUpload, FiAlertTriangle, FiShare2, FiUsers, FiHardDrive, FiCloud } from 'react-icons/fi';
import { isLocalId, listLocal, saveLocal, removeLocal, syncLocalConnections, isSupported as localSupported } from '../services/localConnections';
import { useSelector } from 'react-redux';
import OrgPanel from './org/OrgPanel';
import { orgApi } from '../services/orgApi';
import { SharePanel } from './sharing/ShareDialog';
import { IS_STANDALONE } from '../config/features';
import IncomingShares from './sharing/IncomingShares';
import { sharesApi } from './sharing/sharesApi';
import DriverManagerPanel from './DriverManagerPanel';
import { toast } from 'react-toastify';
import { useConfirm } from './ConfirmDialog';
import { usePrompt } from './PromptDialog';
import { DbmsIcon } from '../utils/dbmsIcon';
import { CONN_TYPES, connTypeColorVars } from '../utils/connectionTypes';
import AppModal from './common/AppModal';
import { menuTitle } from './workspace/WorkspaceDock';
import './ConnectionModal.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/sql-developer';

const EMPTY_FORM = {
  // 저장 위치(웹, 2026-10-07) — cloud: 서버에 암호화 저장(어느 PC 에서나) · local: 이 브라우저에만 암호화 저장(서버엔 쓰는 동안 메모리에만)
  storage: 'cloud',
  // 고객정보·기밀 포함 — 켜면 클라우드 저장을 막는다(서버도 막는다: SENSITIVE_MUST_BE_LOCAL)
  sensitive: false,
  name: '',
  dbType: 'oracle',            // oracle | postgresql | tibero
  host: 'localhost',
  port: '1521',
  service: '',
  user: '',
  password: '',
  poolMin: '1',
  poolMax: '5',
  connType: 'dev',
  // B1: Default schema — 연결 후 자동으로 `ALTER SESSION SET CURRENT_SCHEMA` (Oracle/Tibero)
  //                    또는 `SET search_path` (PostgreSQL) 실행
  defaultSchema: '',
  // PDB(Pluggable Database) 이름 — CDB 루트로 접속하는 연결에서, 세션 생성 시 자동으로
  // `ALTER SESSION SET CONTAINER = <pdbName>` 실행(defaultSchema 보다 먼저 적용됨). Oracle 전용.
  pdbName: '',
  // B2: Folder — 사이드바에서 그룹핑 (예: "Production/APAC")
  folder: '',
  // B3: 읽기전용 플래그 (connType=edu 와 별개) — 모든 DML/DDL 거부
  readOnly: false,
  // 공용 연결을 비관리자에게 열 때 쓸 **읽기 전용 DB 계정**. 비워 두면 앱이 막고,
  // 채워 두면 그 계정으로 붙어 **DB 가** 막는다(겹으로 — 앱을 뚫려도 DB 가 거부한다).
  readOnlyUser: '',
  readOnlyPassword: '',
  // B4: 비밀번호 저장 옵션 — false 시 connections.json 에 비밀번호 저장 안 함
  savePassword: true,
  // Oracle 드라이버 모드 (thin: 기본·Client 불필요 / thick: oci.dll 필요·구형 DB 호환)
  driverMode: 'thin',
  // 보안 옵션 (폐쇄망 환경용)
  useDirectConnectString: false,
  connectStringDirect: '',
  enableSsl: false,
  sslServerDNMatch: true,
  // D2: SSL 인증서 (PEM 텍스트 또는 파일 경로)
  sslCaCert: '',         // CA 인증서 체인 (서버 검증용)
  sslClientCert: '',     // 클라이언트 인증서 (mTLS)
  sslClientKey: '',      // 클라이언트 개인키 (mTLS)
  expireTime: '',
  httpsProxyHost: '',
  httpsProxyPort: '',
  connectTimeout: '',
  queueTimeout: '',
  // SSH 터널 (폐쇄망 / bastion host 경유)
  sshEnable: false,
  sshHost: '',
  sshPort: '22',
  sshUser: '',
  sshAuth: 'password',       // 'password' | 'privateKey'
  sshPassword: '',
  sshPrivateKey: '',
  sshPassphrase: '',
  // 드라이버 속성 (임의 key-value) — JSON 저장
  driverProps: {},           // 객체 그대로 유지, UI 는 rows 배열로 렌더
  // Session boot SQL — 연결 후 자동 실행할 SQL 목록
  bootStatements: [],        // 문자열 배열
  // D4: Shell commands — OS 레벨 스크립트 실행 (보안 주의)
  shellBefore: '',           // 풀 생성 직전
  shellAfter: '',            // 풀 생성 직후
  shellDisconnect: '',       // 풀 종료 직후
};

// DB_TYPES 는 백엔드 /dbtypes 엔드포인트에서 동적 로드 (Phase C1).
// 최초 로드 전 초기값으로 기존 하드코딩 유지 — fallback 용.
const getDefaultDbTypes = (t) => [
  { value: 'oracle',     label: 'Oracle',     color: '#e74c3c', defaultPort: '1521', servicePlaceholder: t('connection:dbType.oraclePlaceholder'), serviceLabel: t('connection:dbType.oracleServiceLabel'), available: true },
  { value: 'postgresql', label: 'PostgreSQL', color: '#336791', defaultPort: '5432', servicePlaceholder: t('connection:dbType.pgPlaceholder'),          serviceLabel: t('connection:dbType.pgServiceLabel'),    available: true },
  { value: 'tibero',     label: 'Tibero',     color: '#6f42c1', defaultPort: '8629', servicePlaceholder: t('connection:dbType.tiberoPlaceholder'),                serviceLabel: 'SID',             available: true, sqlExecution: false },
  { value: 'sqlite',     label: 'SQLite',     color: '#003B57', defaultPort: '',     servicePlaceholder: t('connection:dbType.filePath'),                        serviceLabel: t('connection:dbType.filePath'),       available: true },
];

// manifest (백엔드 응답) → 프론트 DB_TYPES 형식 변환
function _manifestToDbType(m, t) {
  return {
    value: m.dbType,
    label: m.name,
    color: m.color,
    defaultPort: m.defaultPort || '',
    servicePlaceholder: m.servicePlaceholder || '',
    serviceLabel: m.serviceLabel || t('connection:dbType.serviceFallback'),
    supportsFeatures: m.supportsFeatures || {},
    available: m.available !== false,
    // 연결 테스트만 되는 DBMS(execute 미구현) — 서버 manifest 가 말한다
    sqlExecution: m.sqlExecution !== false,
    note: m.note,
  };
}

const ConnectionModal = ({ isOpen, onClose, onConnected, onSaved, activeConnectionId, initialSelectedId, apiBaseUrl }) => {
  const { t } = useTranslation();
  const API = apiBaseUrl || API_BASE;
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [connections, setConnections] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  // 연결 공유(2026-10-03) — 내 개인 연결을 건네고(사용 권한 / 접속 정보만), 받은 것은 「추가」 해야 목록에 들어온다
  const shareApi = React.useMemo(() => sharesApi(API), [API]);
  // 「연결 공유 관리」 탭(2026-10-03 — 창 대신 탭으로): 공유할 내 연결 · 방식 · 받은 공유
  const [shareTargetId, setShareTargetId] = useState(null);
  const [shareMode, setShareMode] = useState('use');          // 'use' | 'copy'
  const [shareReadOnly, setShareReadOnly] = useState(true);
  const [incomingKey, setIncomingKey] = useState(0);
  const [pendingIncoming, setPendingIncoming] = useState(0);
  // 조직(2026-10-03) — 내가 든 조직. 연결을 조직에 공개하는 선택과 「조직」 탭이 쓴다. 스탠드얼론에는 없다
  const orgs = React.useMemo(() => orgApi(API), [API]);
  const authUser = useSelector(st => (st.auth && st.auth.user) || null);
  const meUsrid = authUser ? (authUser.usrid || authUser.id || null) : null;
  const [myOrgs, setMyOrgs] = useState([]);
  const [orgKey, setOrgKey] = useState(0);
  useEffect(() => {
    if (!isOpen || IS_STANDALONE) return;
    orgs.mine().then(list => setMyOrgs(list)).catch(() => setMyOrgs([]));
  }, [isOpen, orgs, orgKey]);
  const pendingOrgRequests = myOrgs.reduce((n, o) => n + (o.pending || 0), 0);
  const adminOrgs = myOrgs.filter(o => o.status === 'active' && o.role === 'admin');
  useEffect(() => {
    if (!isOpen || IS_STANDALONE) return;        // 혼자 쓰는 앱에는 받을 공유가 없다
    shareApi.incoming(['conn-use', 'conn-copy'])
      .then(rows => setPendingIncoming(rows.filter(r => r.status === 'pending').length))
      .catch(() => setPendingIncoming(0));
  }, [isOpen, shareApi, incomingKey]);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [isNew, setIsNew] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectedIds, setConnectedIds] = useState(new Set());
  // 폼에 저장하지 않은 수정이 있는지 — 연결 시 저장본 대신 수정본을 반영하기 위한 플래그.
  const [dirty, setDirty] = useState(false);
  // 탭 선택 (connections | drivers)
  const [activeTab, setActiveTab] = useState('connections');
  // Phase C1: DB 타입 목록 (백엔드 /dbtypes 에서 로드)
  const [dbTypes, setDbTypes] = useState(() => getDefaultDbTypes(t));
  // 접힘 섹션 상태 — 기본 확장 여부 지정
  const [sections, setSections] = useState({
    basic: true,       // 기본 정보 — 기본 열림
    conn: true,        // 연결 정보 — 기본 열림
    pool: false,       // 풀 설정 — 기본 닫힘
    advanced: false,   // 고급 보안 설정 — 기본 닫힘 (드라이버 모드·SSL·프록시 등 포함)
  });
  const toggleSection = (key) => setSections(p => ({ ...p, [key]: !p[key] }));

  // B2: 사이드바 검색·폴더 그룹핑 state
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedFolders, setCollapsedFolders] = useState({});  // { 'Production/APAC': true }
  const toggleFolder = (folderPath) =>
    setCollapsedFolders(p => ({ ...p, [folderPath]: !p[folderPath] }));

  // 필터링 + 폴더 그룹핑 — 렌더에서 사용
  const groupedConnections = (() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = connections.filter(c => {
      if (!q) return true;
      return (
        (c.name || '').toLowerCase().includes(q) ||
        (c.host || '').toLowerCase().includes(q) ||
        (c.service || '').toLowerCase().includes(q) ||
        (c.folder || '').toLowerCase().includes(q) ||
        (c.user || '').toLowerCase().includes(q)
      );
    });
    // 폴더별 그룹: '' 은 루트
    const groups = new Map();
    for (const c of filtered) {
      const folder = (c.folder || '').trim();
      if (!groups.has(folder)) groups.set(folder, []);
      groups.get(folder).push(c);
    }
    // 루트를 먼저, 나머지는 알파벳 순
    return [...groups.entries()].sort((a, b) => {
      if (a[0] === '') return -1;
      if (b[0] === '') return 1;
      return a[0].localeCompare(b[0]);
    });
  })();

  // Import/Export refs & handlers
  const importFileRef = useRef(null);
  const handleExport = useCallback(async () => {
    const includePasswords = await confirm({
      title: t('connection:export.title'),
      message: t('connection:export.includePasswordQuestion'),
      confirmText: t('connection:export.include'),
      cancelText: t('connection:export.exclude'),
      variant: 'danger',
    });
    // 내보내기 파일은 **늘 암호로 담긴다**(2026-10-07) — 비밀번호를 빼도 호스트·계정이 실린다. 로컬 전용 연결은 실리지 않는다
    const passphrase = await prompt({
      title: t('connection:export.passTitle'),
      message: t('connection:export.passMessage'),
      placeholder: t('connection:export.passPlaceholder'),
      inputType: 'password',
    });
    if (passphrase === null) return;
    if (passphrase.length < 8) { toast.error(t('connection:export.passTooShort')); return; }
    const again = await prompt({ title: t('connection:export.passTitle'), message: t('connection:export.passAgain'), inputType: 'password' });
    if (again === null) return;
    if (again !== passphrase) { toast.error(t('connection:export.passMismatch')); return; }
    try {
      // axios 로 blob 을 받아 in-app 다운로드.
      // window.open(http URL) 은 Electron 의 setWindowOpenHandler 가 외부 브라우저로
      // 넘겨버려 스탠드얼론에서 브라우저가 열림 → blob 방식으로 앱 내부 다운로드.
      // 암호는 본문으로(POST) — 쿼리 문자열은 프록시·접근 로그에 남는다
      const res = await axios.post(`${API}/connections/export`, { includePasswords, passphrase }, { responseType: 'blob' });
      const stamp = new Date().toISOString().slice(0, 10);
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `livesql-connections-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error(t('connection:export.failed', { message: err?.message || err }));
    }
  }, [API, confirm, prompt]);
  const handleImportClick = () => importFileRef.current?.click();
  const handleImportFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      // 암호로 담긴 파일(2026-10-07) — 암호를 물어 서버가 연다. 옛 평문 파일도 그대로 받는다
      let passphrase = null;
      if (parsed && parsed.encrypted && parsed.box) {
        passphrase = await prompt({ title: t('connection:import.passTitle'), message: t('connection:import.passMessage'), inputType: 'password' });
        if (passphrase === null) return;
      }
      const connections = parsed.box ? null : (parsed.connections || parsed);  // 둘 다 허용
      if (!parsed.box && !Array.isArray(connections)) {
        toast.error(t('connection:import.invalidJson'));
        return;
      }
      const merge = await confirm({
        title: t('connection:import.title'),
        message: t('connection:import.confirm', { count: parsed.box ? (parsed.count || '?') : connections.length }),
        confirmText: t('connection:import.merge'),
        cancelText: t('connection:import.replace'),
      });
      const mode = merge ? 'merge' : 'replace';
      const res = await axios.post(`${API}/connections/import`, parsed.box ? { box: parsed.box, passphrase, mode } : { connections, mode });
      toast.info(res.data.message || t('connection:import.done'));
      // 목록 새로고침 — loadConnections 가 정의되어 있음 (아래 참조)
      if (typeof loadConnections === 'function') await loadConnections();
    } catch (err) {
      toast.error(t('connection:import.failed', { message: formatApiError(err, t) }));
    }
  }, [API, confirm, prompt]);

  // 창 끌기는 없앴다 — 작업 탭으로 연다(2026-11-04 사용자 요청)

  // 연결 목록 로드
  const loadConnections = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/connections`);
      setConnections(res.data);
      return Array.isArray(res.data) ? res.data : null;
    } catch (err) {
      console.error('Failed to load connections:', err);
      return null;
    }
  }, []);

  // Phase C1: DB 타입 메타데이터 로드
  const loadDbTypes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dbtypes`);
      if (Array.isArray(res.data) && res.data.length > 0) {
        setDbTypes(res.data.map(m => _manifestToDbType(m, t)));
      }
    } catch (err) {
      console.warn('Failed to load dbTypes, falling back to defaults:', err.message);
    }
  }, [API]);

  useEffect(() => {
    if (isOpen) {
      loadConnections();
      loadDbTypes();
      if (activeConnectionId) {
        setConnectedIds(prev => new Set(prev).add(activeConnectionId));
      }
    }
  }, [isOpen, loadConnections, loadDbTypes, activeConnectionId]);

  // 편집 버튼으로 열린 경우 — connections 로드 완료 후 해당 항목 자동 선택.
  useEffect(() => {
    if (isOpen && initialSelectedId && Array.isArray(connections) && connections.length > 0) {
      const target = connections.find(c => c.id === initialSelectedId);
      if (target) handleSelect(target);
    }
    // handleSelect 는 deps=[] 인 useCallback 이라 안정적. initialSelectedId 변경 시 재실행.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialSelectedId, connections]);

  const selectedConn = connections.find(c => c.id === selectedId) || null;

  // 연결 선택
  const handleSelect = useCallback((conn) => {
    setSelectedId(conn.id);
    setIsNew(false);
    setTestResult(null);
    setDirty(false);
    setSections(p => ({ ...p, advanced: false }));
    setForm({
      storage: isLocalId(conn.id) ? 'local' : 'cloud',
      sensitive: !!conn.sensitive,
      name: conn.name || '',
      dbType: conn.dbType || 'oracle',
      host: conn.host || 'localhost',
      port: String(conn.port || 1521),
      service: conn.service || '',
      user: conn.user || '',
      // 서버는 비밀번호를 내려주지 않는다(hasPassword 여부만). 빈 칸으로 두고,
      // 저장할 때 손대지 않았으면 **키를 아예 빼서** 기존 값을 그대로 둔다.
      password: '',
      poolMin: String(conn.poolMin || 1),
      poolMax: String(conn.poolMax || 5),
      connType: conn.connType || 'dev',
      defaultSchema: conn.defaultSchema || '',
      pdbName: conn.pdbName || '',
      folder: conn.folder || '',
      readOnly: conn.readOnly || false,
      readOnlyUser: conn.readOnlyUser || '',
      // 비밀번호는 목록에 실리지 않는다(redactConnection) — 비워 두면 저장된 값을 유지한다
      readOnlyPassword: '',
      savePassword: conn.savePassword !== false,  // 기본 true
      driverMode: conn.driverMode || 'thin',
      // 보안 옵션 - 역호환 처리 (기존 데이터에 없으면 기본값)
      useDirectConnectString: conn.useDirectConnectString || false,
      connectStringDirect: conn.connectStringDirect || '',
      enableSsl: conn.enableSsl || false,
      sslServerDNMatch: conn.sslServerDNMatch !== false,
      sslCaCert: conn.sslCaCert || '',
      sslClientCert: conn.sslClientCert || '',
      sslClientKey: conn.sslClientKey || '',
      expireTime: conn.expireTime ? String(conn.expireTime) : '',
      httpsProxyHost: conn.httpsProxyHost || '',
      httpsProxyPort: conn.httpsProxyPort ? String(conn.httpsProxyPort) : '',
      connectTimeout: conn.connectTimeout ? String(conn.connectTimeout) : '',
      queueTimeout: conn.queueTimeout ? String(conn.queueTimeout) : '',
      // SSH 터널
      sshEnable: conn.sshEnable || false,
      sshHost: conn.sshHost || '',
      sshPort: String(conn.sshPort || 22),
      sshUser: conn.sshUser || '',
      sshAuth: conn.sshAuth || 'password',
      sshPassword: conn.sshPassword || '',
      sshPrivateKey: conn.sshPrivateKey || '',
      sshPassphrase: conn.sshPassphrase || '',
      // 드라이버 속성 / boot SQL
      driverProps: conn.driverProps || {},
      bootStatements: Array.isArray(conn.bootStatements) ? conn.bootStatements : [],
      // Shell commands
      shellBefore: conn.shellBefore || '',
      shellAfter: conn.shellAfter || '',
      shellDisconnect: conn.shellDisconnect || '',
    });
  }, []);

  // 새 연결
  const handleNew = useCallback(() => {
    setSelectedId(null);
    setIsNew(true);
    setTestResult(null);
    setDirty(false);
    setSections(p => ({ ...p, advanced: false }));
    setForm({ ...EMPTY_FORM });
  }, []);

  // 폼 변경
  const handleFormChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setTestResult(null);
    setDirty(true);
  }, []);

  // 연결 테스트
  const handleTest = useCallback(async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/connections/test`, {
        dbType: form.dbType,
        host: form.host,
        port: parseInt(form.port),
        service: form.service,
        user: form.user,
        password: form.password,
        driverMode: form.driverMode,
        useDirectConnectString: form.useDirectConnectString,
        connectStringDirect: form.connectStringDirect,
        enableSsl: form.enableSsl,
        sslServerDNMatch: form.sslServerDNMatch,
        connectTimeout: form.connectTimeout ? parseInt(form.connectTimeout) : undefined,
        httpsProxyHost: form.httpsProxyHost || undefined,
        httpsProxyPort: form.httpsProxyPort ? parseInt(form.httpsProxyPort) : undefined,
        // SSH 터널
        sshEnable: form.sshEnable,
        sshHost: form.sshHost || undefined,
        sshPort: form.sshPort ? parseInt(form.sshPort) : undefined,
        sshUser: form.sshUser || undefined,
        sshAuth: form.sshAuth,
        sshPassword: form.sshPassword || undefined,
        sshPrivateKey: form.sshPrivateKey || undefined,
        sshPassphrase: form.sshPassphrase || undefined,
      });
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setIsTesting(false);
    }
  }, [API, form]);

  // 저장
  const handleSave = useCallback(async () => {
    // 비밀번호 세 갈래.
    //   savePassword=false  → null 을 **명시적으로** 보낸다 (저장하지 않겠다는 의사)
    //   새로 입력함          → 그 값
    //   빈 칸 + 이미 저장돼 있음 → **키를 뺀다.** 서버의 updateConnection 이 스프레드라
    //                            키가 없으면 기존 값이 그대로 살아남는다. 빈 문자열을
    //                            보내면 저장된 비밀번호를 지워 연결이 깨진다.
    const keepStoredPassword = form.savePassword !== false && !form.password && !!(connections.find(c => c.id === selectedId) || {}).hasPassword;
    const passwordToSave = form.savePassword === false ? null : form.password;
    const payload = {
      name: form.name,
      sensitive: !!form.sensitive,
      dbType: form.dbType,
      host: form.host,
      port: parseInt(form.port),
      service: form.service,
      user: form.user,
      ...(keepStoredPassword ? {} : { password: passwordToSave }),
      poolMin: parseInt(form.poolMin),
      poolMax: parseInt(form.poolMax),
      connType: form.connType,
      defaultSchema: form.defaultSchema || null,
      pdbName: form.pdbName || null,
      folder: form.folder || null,
      readOnly: !!form.readOnly,
      readOnlyUser: (form.readOnlyUser || '').trim(),
      // 빈 값이면 **보내지 않는다** — 보내면 저장된 비밀번호가 지워진다
      ...((form.readOnlyPassword || '').trim() ? { readOnlyPassword: form.readOnlyPassword } : {}),
      savePassword: form.savePassword !== false,
      driverMode: form.driverMode,
      // 보안 옵션
      useDirectConnectString: form.useDirectConnectString,
      connectStringDirect: form.connectStringDirect || null,
      enableSsl: form.enableSsl,
      sslServerDNMatch: form.sslServerDNMatch,
      sslCaCert: form.sslCaCert || null,
      sslClientCert: form.sslClientCert || null,
      sslClientKey: form.sslClientKey || null,
      expireTime: form.expireTime ? parseInt(form.expireTime) : null,
      httpsProxyHost: form.httpsProxyHost || null,
      httpsProxyPort: form.httpsProxyPort ? parseInt(form.httpsProxyPort) : null,
      connectTimeout: form.connectTimeout ? parseInt(form.connectTimeout) : null,
      queueTimeout: form.queueTimeout ? parseInt(form.queueTimeout) : null,
      // SSH 터널
      sshEnable: form.sshEnable,
      sshHost: form.sshHost || null,
      sshPort: form.sshPort ? parseInt(form.sshPort) : null,
      sshUser: form.sshUser || null,
      sshAuth: form.sshAuth,
      sshPassword: form.sshPassword || null,
      sshPrivateKey: form.sshPrivateKey || null,
      sshPassphrase: form.sshPassphrase || null,
      // 드라이버 속성 / boot SQL
      driverProps: form.driverProps || {},
      bootStatements: form.bootStatements || [],
      // Shell commands
      shellBefore: form.shellBefore || null,
      shellAfter: form.shellAfter || null,
      shellDisconnect: form.shellDisconnect || null,
    };

    try {
      let savedConn;
      // 저장 위치(웹) — 민감 연결은 늘 이 브라우저에만(서버도 클라우드 저장을 거부한다)
      const wantLocal = !IS_STANDALONE && (form.storage === 'local' || !!form.sensitive);
      const wasLocal = !isNew && isLocalId(selectedId);
      // 비밀은 빈 칸이면 「저장된 값 유지」 — 로컬로 옮길 때는 서버의 값을 받아 올 수 없으므로 다시 입력해야 한다
      const secretKeys = ['password', 'readOnlyPassword', 'sshPassword', 'sshPrivateKey', 'sshPassphrase'];
      const withoutEmptySecrets = (o) => Object.fromEntries(Object.entries(o).filter(([k, v]) => !(secretKeys.includes(k) && !v)));
      if (wantLocal) {
        if (!localSupported()) { toast.error(t('connection:storage.unsupported')); return null; }
        const prevLocal = wasLocal ? (await listLocal(meUsrid)).find(c => c.id === selectedId) : null;
        const prevCloud = !isNew && !wasLocal ? connections.find(c => c.id === selectedId) : null;
        if (prevCloud && prevCloud.hasPassword && !form.password && form.savePassword !== false) {
          toast.error(t('connection:storage.needPassword'));
          return null;
        }
        const saved = await saveLocal(meUsrid, {
          ...(prevLocal || {}), ...withoutEmptySecrets(payload), storage: 'local', id: wasLocal ? selectedId : undefined,
        });
        await syncLocalConnections(meUsrid);
        // 클라우드 → 로컬: 서버 사본을 지운다(남겨 두면 「로컬에만」 이 거짓이 된다)
        if (prevCloud) await axios.delete(`${API}/connections/${selectedId}`);
        savedConn = { id: saved.id };
      } else if (wasLocal) {
        // 로컬 → 클라우드: 서버에 새로 만들고(비밀은 이 브라우저의 값으로 채운다) 로컬 사본을 지운다
        const prevLocal = (await listLocal(meUsrid)).find(c => c.id === selectedId) || {};
        const res = await axios.post(`${API}/connections`, { ...withoutEmptySecrets(prevLocal), ...withoutEmptySecrets(payload), id: undefined, storage: undefined });
        savedConn = res.data;
        await removeLocal(meUsrid, selectedId);
        await syncLocalConnections(meUsrid);
      } else if (isNew) {
        const res = await axios.post(`${API}/connections`, payload);
        savedConn = res.data;
      } else {
        const res = await axios.put(`${API}/connections/${selectedId}`, payload);
        savedConn = res.data;
      }
      const fresh = await loadConnections();
      // 앱의 연결 목록(redux)에도 알린다 — 이 창의 목록은 창 안의 상태일 뿐이라, 안 알리면
      // 연결 유형(운영→개발)을 바꿔 저장해도 실행 전 확인·색 띠가 옛 값으로 남는다(신고 2026-11-02)
      if (onSaved && fresh) onSaved(fresh);
      // 새로 생성한 경우 해당 항목 선택
      if (isNew && savedConn?.id) {
        setSelectedId(savedConn.id);
      }
      setIsNew(false);
      setTestResult(null);
      setDirty(false);
      toast.success(t('connection:save.success', { name: form.name }));
      return savedConn;   // 저장 후 연결 흐름에서 id 사용
    } catch (err) {
      toast.error(t('connection:save.failed') + (formatApiError(err, t)));
      return null;
    }
  }, [API, form, isNew, selectedId, loadConnections, connections, meUsrid, onSaved]);

  // 삭제
  const handleDelete = useCallback(async () => {
    if (!selectedId) return;
    const ok = await confirm({ title: t('connection:delete.title'), message: t('connection:delete.message'), confirmText: t('connection:delete.confirm'), variant: 'danger' });
    if (!ok) return;
    try {
      if (isLocalId(selectedId)) {
        // 로컬 전용 — 이 브라우저의 암호문을 지우고 서버 메모리에서도 지운다
        await removeLocal(meUsrid, selectedId);
        await axios.delete(`${API}/connections/local/${selectedId}`).catch(() => {});
      } else {
        await axios.delete(`${API}/connections/${selectedId}`);
      }
      setSelectedId(null);
      setForm({ ...EMPTY_FORM });
      setIsNew(false);
      await loadConnections();
      toast.success(t('connection:delete.success'));
    } catch (err) {
      toast.error(t('connection:delete.failed') + (formatApiError(err, t)));
    }
  }, [API, selectedId, loadConnections, meUsrid]);

  // 연결 (풀 생성 + 콜백)
  const handleConnect = useCallback(async () => {
    let connId = selectedId;
    let savedConn = null;

    // 저장하지 않은 수정이 있으면 먼저 저장 — 백엔드 connect 는 저장본 기준으로 연결하므로,
    // 수정본으로 연결하려면 반드시 저장이 선행되어야 함.
    if (dirty) {
      const ok = await confirm({
        title: t('connection:save.unsavedTitle'),
        message: t('connection:save.unsavedMessage'),
        confirmText: t('connection:save.saveAndConnect'),
        cancelText: t('common:action.cancel'),
      });
      if (!ok) return;
      savedConn = await handleSave();
      if (!savedConn || !savedConn.id) return;   // 저장 실패 시 연결 중단
      connId = savedConn.id;
    }

    if (!connId) return;
    // 저장본 우선 — 방금 저장했으면 connections state(비동기 갱신) 대신 응답 객체 사용.
    const conn = savedConn || connections.find(c => c.id === connId);
    // B4: savePassword=false 로 저장한 연결은 비밀번호가 없다 → 사용자에게 프롬프트.
    // 서버가 값을 안 내려주므로 **여부(hasPassword)** 로 판단한다. 값으로 보면 항상
    // undefined 라 프롬프트가 안 뜨고 연결이 그냥 실패한다.
    const storedEmpty = conn && !conn.hasPassword;
    let runtimePassword;
    if (storedEmpty) {
      runtimePassword = await prompt({
        title: t('connection:password.promptTitle'),
        message: t('connection:password.promptMessage', { name: conn.name }),
        defaultValue: '',
        placeholder: t('connection:password.label'),
      });
      if (runtimePassword === null) return;  // 취소
      if (!runtimePassword) {
        toast.error(t('connection:password.empty'));
        return;
      }
    }
    setIsConnecting(true);
    try {
      const res = await axios.post(`${API}/connections/${connId}/connect`, runtimePassword ? { password: runtimePassword } : {});
      if (res.data.success) {
        setConnectedIds(prev => new Set(prev).add(connId));
        if (onConnected) {
          onConnected({
            id: connId,
            name: conn?.name || form.name,
            dbType: conn?.dbType || form.dbType || 'oracle',
            connType: conn?.connType || form.connType || 'dev',
          });
        }
        onClose();
      }
    } catch (err) {
      toast.error(t('connection:action.failed') + (formatApiError(err, t)));
    } finally {
      setIsConnecting(false);
    }
  }, [selectedId, dirty, confirm, handleSave, connections, form.name, form.dbType, form.connType, onConnected, onClose, API, prompt]);

  if (!isOpen) return null;

  // 직접 입력 모드면 host/port/service 불필요
  // 편집 중인 연결에 비밀번호가 저장돼 있으면, 빈 칸은 "그대로 둔다" 는 뜻이다.
  // 서버가 값을 안 내려주므로 여부(hasPassword)로만 안다.
  const keepStoredHint = !isNew && !!(connections.find(c => c.id === selectedId) || {}).hasPassword;

  const isFormValid = form.name && form.user && (
    form.useDirectConnectString
      ? !!form.connectStringDirect
      : (form.host && form.port && form.service)
  );

  return (
    // 작업 탭(2026-11-04 사용자 요청) — 제목은 메뉴 문구(탭을 앞으로 부르는 기준), 절반 폭 · 새 창 분리 없음
    <AppModal windowed noPopout half open={!!isOpen} onClose={onClose} className="conn-modal"
      title={menuTitle(t('menu:connection.manage'))} icon={<FiDatabase size={14} />}
      headerExtra={activeTab === 'connections' ? (
              <div className="conn-modal-header-actions">
                <button className="app-btn" onClick={handleNew}><FiPlus /> {t('connection:field.newConnection')}</button>
                <button className="app-btn" onClick={handleImportClick} title={t('connection:import.tooltip')}>
                  <FiUpload /> {t('connection:act.import')}
                </button>
                <button className="app-btn" onClick={handleExport} title={t('connection:export.tooltip')}>
                  <FiDownload /> {t('connection:act.export')}
                </button>
              </div>
      ) : null}>

        {/* 탭 네비게이션 */}
        <div className="conn-modal-tabs app-modal-tabs">
          <button
            className={`app-modal-tab ${activeTab === 'connections' ? 'active' : ''}`}
            onClick={() => setActiveTab('connections')}
          >
            <FiDatabase size={13} /> {t('connection:field.tabConnections')}
          </button>
          <button
            className={`app-modal-tab ${activeTab === 'drivers' ? 'active' : ''}`}
            onClick={() => setActiveTab('drivers')}
          >
            <FiPackage size={13} /> {t('connection:field.tabDrivers')}
          </button>
          {/* 사용자 간 공유는 웹의 것 — 스탠드얼론은 혼자라 탭이 뜻이 없다(config/features.js IS_STANDALONE) */}
          {!IS_STANDALONE && (
            <button
              className={`app-modal-tab ${activeTab === 'sharing' ? 'active' : ''}`}
              onClick={() => setActiveTab('sharing')}
            >
              <FiShare2 size={13} /> {t('common:share.tabTitle')}
              {pendingIncoming > 0 && <span className="conn-tab-count" title={t('common:share.incomingTitle', { n: pendingIncoming })}>{pendingIncoming}</span>}
            </button>
          )}
          {!IS_STANDALONE && (
            <button
              className={`app-modal-tab ${activeTab === 'orgs' ? 'active' : ''}`}
              onClick={() => setActiveTab('orgs')}
            >
              <FiUsers size={13} /> {t('common:org.tabTitle')}
              {pendingOrgRequests > 0 && <span className="conn-tab-count" title={t('common:org.pendingCount', { n: pendingOrgRequests })}>{pendingOrgRequests}</span>}
            </button>
          )}
        </div>

        {/* 조직 탭 — 내 조직 · 만들기 · 초대코드 신청 · 구성원 관리(관리자) */}
        {activeTab === 'orgs' && !IS_STANDALONE && (
          <div className="conn-share-tab">
            <OrgPanel api={orgs} apiBase={API} meUsrid={meUsrid} onChanged={() => { setOrgKey(k => k + 1); loadConnections(); }} />
          </div>
        )}

        {/* 연결 공유 관리 탭 — 받은 공유를 추가하고, 내 개인 연결을 건넨다 */}
        {activeTab === 'sharing' && !IS_STANDALONE && (
          <div className="conn-share-tab">
            <IncomingShares api={shareApi} kinds={['conn-use', 'conn-copy']} reloadKey={incomingKey}
              onAccepted={async () => { await loadConnections(); setIncomingKey(k => k + 1); }} />
            <div className="conn-share-split">
              <div className="conn-share-list">
                <div className="conn-share-list-head">{t('common:share.myConnections')}</div>
                {connections.filter(c => c.isMine).length === 0 && (
                  <div className="conn-share-empty">{t('common:share.noMyConnections')}</div>
                )}
                {connections.filter(c => c.isMine).map(c => (
                  <button
                    key={c.id}
                    type="button"
                    className={`conn-share-item ${shareTargetId === c.id ? 'active' : ''}`}
                    onClick={() => setShareTargetId(c.id)}
                    title={`${c.user || ''}@${c.host || ''}:${c.port || ''}/${c.service || ''}`}
                  >
                    <DbmsIcon dbType={c.dbType} size={14} style={{ marginRight: 6 }} />
                    <span>{c.name}</span>
                  </button>
                ))}
              </div>
              <div className="conn-share-main">
                {(() => {
                  const target = connections.find(c => c.id === shareTargetId && c.isMine);
                  if (!target) return <div className="conn-share-empty">{t('common:share.pickConnection')}</div>;
                  return (
                    <>
                      <div className="conn-share-target"><FiShare2 size={13} /> {t('common:share.connTitle', { name: target.name })}</div>
                      <SharePanel
                        api={shareApi}
                        resetKey={target.id}
                        ownerUsrid={target.ownerUsrid}
                        hint={t('common:share.connDialogHint')}
                        load={() => shareApi.connShares(target.id)}
                        submit={(usrids) => shareApi.shareConn(target.id, { toUsrids: usrids, mode: shareMode, readOnly: shareReadOnly })}
                        options={(
                          <div className="share-options">
                            <label>
                              <input type="radio" name="conn-share-mode" checked={shareMode === 'use'} onChange={() => setShareMode('use')} />
                              <span><b>{t('common:share.modeUse')}</b> — {t('common:share.modeUseDesc')}</span>
                            </label>
                            {shareMode === 'use' && (
                              <label className="share-sub">
                                <input type="checkbox" checked={!shareReadOnly} onChange={(e) => setShareReadOnly(!e.target.checked)} />
                                <span>{t('common:share.allowWrite')}</span>
                              </label>
                            )}
                            <label>
                              <input type="radio" name="conn-share-mode" checked={shareMode === 'copy'} onChange={() => setShareMode('copy')} />
                              <span><b>{t('common:share.modeCopy')}</b> — {t('common:share.modeCopyDesc')}</span>
                            </label>
                          </div>
                        )}
                      />
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* 드라이버 관리 탭 */}
        {activeTab === 'drivers' && (
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            <DriverManagerPanel apiBaseUrl={API} />
          </div>
        )}

        {/* 본문: 좌(목록) + 우(설정) — 연결 관리 탭일 때만 렌더 */}
        {activeTab === 'connections' && (
        <div className="conn-modal-body">
          {/* 좌측: 연결 목록 — 검색 + 폴더 그룹핑 */}
          <div className="conn-modal-sidebar">
            <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border, #e0e0e0)' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={t('connection:list.searchPlaceholder')}
                style={{
                  width: '100%', padding: '5px 8px', fontSize: 12,
                  border: '1px solid var(--color-border, #ccc)', borderRadius: 4,
                  background: 'var(--color-bg, #fff)', color: 'var(--color-text, #333)',
                }}
              />
            </div>
            <div className="conn-sidebar-list">
              {groupedConnections.map(([folder, list]) => (
                <div key={folder || '__root__'}>
                  {folder && (
                    <div
                      onClick={() => toggleFolder(folder)}
                      style={{
                        padding: '4px 8px', fontSize: 11, fontWeight: 600,
                        color: 'var(--color-text-secondary, #555)',
                        cursor: 'pointer', userSelect: 'none',
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      {collapsedFolders[folder] ? <FiChevronRight size={10} /> : <FiChevronDown size={10} />}
                      <span>📁 {folder}</span>
                      <span style={{ marginLeft: 'auto', color: '#888' }}>{list.length}</span>
                    </div>
                  )}
                  {!collapsedFolders[folder] && list.map(conn => (
                    <div
                      key={conn.id}
                      className={`conn-sidebar-item ${selectedId === conn.id ? 'active' : ''} ${connectedIds.has(conn.id) ? 'connected' : ''}`}
                      onClick={() => handleSelect(conn)}
                      style={folder ? { paddingLeft: 24 } : undefined}
                      title={`${conn.user || ''}@${conn.host || ''}:${conn.port || ''}/${conn.service || ''}`}
                    >
                      <span className="conn-status-dot" />
                      <DbmsIcon dbType={conn.dbType} size={16} style={{ marginRight: 6 }} />
                      <span>{conn.name}</span>
                      {isLocalId(conn.id) && (
                        <FiHardDrive size={11} className="conn-shared-mark" title={t('connection:storage.localBadge')} />
                      )}
                      {conn.sharedFrom && (
                        <FiShare2 size={11} className="conn-shared-mark" title={t('common:share.sharedToYou')} />
                      )}
                      {conn.org && (
                        <FiUsers size={11} className="conn-shared-mark" title={t('common:org.connBadge', { name: conn.org.name })} />
                      )}
                      {conn.readOnly && (
                        <span style={{ marginLeft: 'auto', fontSize: 9, padding: '1px 5px', border: '1px solid #888', borderRadius: 3, color: '#888' }} title={t('connection:list.readOnly')}>RO</span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
              {groupedConnections.length === 0 && (
                <div style={{ padding: '20px', color: '#999', fontSize: 13, textAlign: 'center' }}>
                  {searchQuery ? t('connection:list.noSearchResult') : t('connection:list.empty')}
                </div>
              )}
            </div>
          </div>

          {/* 우측: 상세/편집 */}
          <div className="conn-modal-detail">

          {(selectedId || isNew) ? (
            <>
              <div className="conn-detail-body">
                {selectedConn && selectedConn.sharedFrom && (
                  <div className="conn-shared-note">
                    <FiShare2 size={12} /> {selectedConn.grantReadOnly ? t('common:share.grantedNoteRo') : t('common:share.grantedNoteRw')}
                  </div>
                )}
                <div className="conn-info-box">
                  {form.dbType === 'postgresql'
                    ? t('connection:driver.postgres')
                    : form.dbType === 'tibero'
                    ? t('connection:driver.tibero')
                    : t('connection:driver.oracle')}
                </div>
                {/* DBMS 종류 선택 */}
                <div className="conn-form-group">
                  <label>{t('connection:field.dbType')}</label>
                  <div className="conn-type-group">
                    {dbTypes.map(db => (
                      <button
                        key={db.value}
                        type="button"
                        className={`conn-type-btn ${form.dbType === db.value ? 'active' : ''}`}
                        onClick={() => {
                          handleFormChange('dbType', db.value);
                          // 이전 포트가 기본값이면 새 DBMS 기본값으로 교체
                          const prev = dbTypes.find(d => d.defaultPort === form.port);
                          if (prev) handleFormChange('port', db.defaultPort);
                        }}
                        title={db.available === false ? t('connection:driver.notInstalled', { label: db.label }) : db.label}
                        style={db.available === false ? { opacity: 0.5 } : (form.dbType === db.value ? { borderColor: db.color, color: db.color } : {})}
                      >
                        {db.label}{db.available === false ? t('connection:driver.needInstall') : (db.sqlExecution === false ? t('connection:driver.testOnlyTag') : '')}
                      </button>
                    ))}
                  </div>
                  {form.dbType === 'tibero' && (
                    <div style={{ fontSize: 11, color: '#6f42c1', marginTop: 4 }}>
                      {t('connection:driver.tiberoNotice')}{' '}
                      <code>Tibero 7 ODBC Driver</code>. {t('connection:driver.tiberoOverride')}
                    </div>
                  )}
                  {(() => {
                    const sel = dbTypes.find(d => d.value === form.dbType);
                    if (sel && sel.available !== false && sel.sqlExecution === false) {
                      return (
                        <div style={{ fontSize: 11, color: '#92400e', marginTop: 4 }}>
                          <FiAlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />{t('connection:driver.testOnlyNote', { label: sel.label })}
                        </div>
                      );
                    }
                    if (sel && sel.available === false) {
                      return (
                        <div style={{ fontSize: 11, color: '#dc3545', marginTop: 4 }}>
                          <FiAlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /><strong>{sel.label}</strong>{' '}
                          {t('connection:driver.missingPrefix')}(<code>{sel.value}</code>){t('connection:driver.missingSuffix')}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div className="conn-form-row">
                  <div className="conn-form-group" style={{ flex: 2 }}>
                    <label>{t('connection:field.name')}</label>
                    <input value={form.name} onChange={e => handleFormChange('name', e.target.value)} placeholder={t('connection:form.namePlaceholder')} />
                  </div>
                  <div className="conn-form-group" style={{ flex: 1 }}>
                    <label>{t('connection:field.connType')}</label>
                    <div className="conn-type-group">
                      {CONN_TYPES.map(ct => (
                        <button
                          key={ct.value}
                          className={`conn-type-btn ${form.connType === ct.value ? 'active' : ''}`}
                          style={form.connType === ct.value ? connTypeColorVars(ct) : {}} /* 색은 CSS 가 고른다 — 다크모드는 밝은 색 */
                          onClick={() => handleFormChange('connType', ct.value)}
                          title={t(ct.descKey)}
                          type="button"
                        >
                          {t(ct.labelKey)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="conn-form-group" style={{ display: 'none' }}>
                  {/* 기존 연결 이름 input 제거용 placeholder */}
                </div>
                {/* 저장 위치(웹) — 클라우드(서버에 암호화) / 이 브라우저에만. 민감 연결은 클라우드를 고를 수 없다 */}
                {!IS_STANDALONE ? (
                  <div className="conn-form-group conn-storage">
                    <label>{t('connection:storage.label')}</label>
                    <div className="conn-storage-row">
                      <label className="conn-storage-opt" title={t('connection:storage.cloudTip')}>
                        <input type="radio" name="conn-storage" checked={form.storage !== 'local' && !form.sensitive}
                          disabled={!!form.sensitive} onChange={() => handleFormChange('storage', 'cloud')} />
                        <FiCloud size={12} /> {t('connection:storage.cloud')}
                      </label>
                      <label className="conn-storage-opt" title={t('connection:storage.localTip')}>
                        <input type="radio" name="conn-storage" checked={form.storage === 'local' || !!form.sensitive}
                          onChange={() => handleFormChange('storage', 'local')} />
                        <FiHardDrive size={12} /> {t('connection:storage.local')}
                      </label>
                      <label className="conn-storage-opt conn-sensitive" title={t('connection:storage.sensitiveTip')}>
                        <input type="checkbox" checked={!!form.sensitive} onChange={(e) => handleFormChange('sensitive', e.target.checked)} />
                        {t('connection:storage.sensitive')}
                      </label>
                    </div>
                    <div className="conn-hint">
                      {form.storage === 'local' || form.sensitive ? t('connection:storage.localHint') : t('connection:storage.cloudHint')}
                    </div>
                  </div>
                ) : null}
                <div className="conn-form-row">
                  <div className="conn-form-group" style={{ flex: 2 }}>
                    <label>{t('connection:field.host')}</label>
                    <input value={form.host} onChange={e => handleFormChange('host', e.target.value)} placeholder="localhost" />
                  </div>
                  <div className="conn-form-group small">
                    <label>{t('connection:field.port')}</label>
                    <input value={form.port} onChange={e => handleFormChange('port', e.target.value)} placeholder="1521" />
                  </div>
                  <div className="conn-form-group" style={{ flex: 2 }}>
                    <label>{form.dbType === 'postgresql' ? t('connection:dbType.pgServiceLabel') : t('connection:dbType.oracleServiceLabel')}</label>
                    <input
                      value={form.service}
                      onChange={e => handleFormChange('service', e.target.value)}
                      placeholder={(dbTypes.find(d => d.value === form.dbType) || dbTypes[0]).servicePlaceholder}
                    />
                  </div>
                </div>
                <div className="conn-form-row">
                  <div className="conn-form-group">
                    <label>{t('connection:field.user')}</label>
                    <input value={form.user} onChange={e => handleFormChange('user', e.target.value)} placeholder={t('connection:form.dbUser')} />
                  </div>
                  <div className="conn-form-group">
                    <label>{t('connection:field.password')}{form.savePassword === false && <span style={{ color: '#dc3545', fontSize: 10, marginLeft: 6 }}>{t('connection:field.notStoredShort')}</span>}</label>
                    <input type="password" value={form.password} onChange={e => handleFormChange('password', e.target.value)} placeholder={form.savePassword === false ? t('connection:password.askEveryTime') : (keepStoredHint ? t('connection:password.keepStored') : t('connection:password.promptTitle'))} />
                  </div>
                </div>
                {/* B1/B2: Default schema + Folder */}
                <div className="conn-form-row">
                  <div className="conn-form-group">
                    <label>{t('connection:field.defaultSchema')} <span style={{ fontSize: 10, color: '#888', fontWeight: 400 }}>{t('connection:field.optional')}</span></label>
                    <input
                      value={form.defaultSchema}
                      onChange={e => handleFormChange('defaultSchema', e.target.value)}
                      placeholder={form.dbType === 'postgresql' ? 'public (search_path)' : 'HR (CURRENT_SCHEMA)'}
                    />
                  </div>
                  <div className="conn-form-group">
                    <label>{t('connection:field.folder')} <span style={{ fontSize: 10, color: '#888', fontWeight: 400 }}>{t('connection:field.folderHint')}</span></label>
                    <input value={form.folder} onChange={e => handleFormChange('folder', e.target.value)} placeholder={t('connection:form.folderPath')} />
                  </div>
                </div>
                {form.dbType === 'oracle' && (
                  <div className="conn-form-row">
                    <div className="conn-form-group">
                      <label>{t('connection:field.pdbName')} <span style={{ fontSize: 10, color: '#888', fontWeight: 400 }}>{t('connection:field.pdbHint')}</span></label>
                      <input
                        value={form.pdbName}
                        onChange={e => handleFormChange('pdbName', e.target.value)}
                        placeholder={t('connection:form.pdbPlaceholder')}
                      />
                    </div>
                  </div>
                )}
                {/* B3/B4: 읽기전용 + 비밀번호 저장 옵션 */}
                <div className="conn-form-row" style={{ alignItems: 'center', gap: 20 }}>
                  <div className="conn-toggle-row" style={{ margin: 0 }}>
                    <input
                      type="checkbox"
                      id="readOnly"
                      checked={form.readOnly}
                      onChange={e => handleFormChange('readOnly', e.target.checked)}
                    />
                    <label htmlFor="readOnly" title={t('connection:form.readOnlyDesc')}>{t('connection:field.readOnly')}</label>
                  </div>
                  <div className="conn-toggle-row" style={{ margin: 0 }}>
                    <input
                      type="checkbox"
                      id="savePassword"
                      checked={form.savePassword !== false}
                      onChange={e => handleFormChange('savePassword', e.target.checked)}
                    />
                    <label htmlFor="savePassword" title={t('connection:password.notStored')}>{t('connection:field.savePassword')}</label>
                  </div>
                </div>
                {/*
                  읽기 전용 DB 계정 — **공용 연결에서만 뜻이 있다.** 주인 없는 연결(교육DB 등)은
                  관리자가 아니면 읽기 전용으로 열리는데, 그때 **이 계정으로** 붙는다.
                  비워 두면 원래 계정으로 붙고 앱이 SELECT 만 통과시킨다 — 그것도 막기는 하지만
                  앱을 뚫으면 끝이다. DB 에서 SELECT 권한만 준 계정을 여기 적는 것이 정석이다.
                */}
                <div className="conn-form-row">
                  <div className="conn-form-group">
                    <label htmlFor="readOnlyUser">{t('connection:field.readOnlyUser')}</label>
                    <input
                      type="text"
                      id="readOnlyUser"
                      value={form.readOnlyUser}
                      onChange={e => handleFormChange('readOnlyUser', e.target.value)}
                      placeholder={t('connection:form.readOnlyUserPlaceholder')}
                    />
                  </div>
                  <div className="conn-form-group">
                    <label htmlFor="readOnlyPassword">{t('connection:field.readOnlyPassword')}</label>
                    <input
                      type="password"
                      id="readOnlyPassword"
                      value={form.readOnlyPassword}
                      onChange={e => handleFormChange('readOnlyPassword', e.target.value)}
                      placeholder={t('connection:form.keepStored')}
                      autoComplete="new-password"
                    />
                  </div>
                </div>
                <div className="conn-hint">{t('connection:form.readOnlyUserDesc')}</div>
                {/* 풀 설정 — 접힘 섹션 */}
                <div className={`conn-section ${sections.pool ? 'expanded' : ''}`}>
                  <div className="conn-section-header" onClick={() => toggleSection('pool')}>
                    <span className="conn-section-title">
                      {sections.pool ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
                      {t('connection:note.poolSummary', { min: form.poolMin, max: form.poolMax })}
                    </span>
                  </div>
                  {sections.pool && (
                    <div className="conn-section-body">
                      <div className="conn-form-row">
                        <div className="conn-form-group small">
                          <label>{t('connection:field.poolMin')}</label>
                          <input value={form.poolMin} onChange={e => handleFormChange('poolMin', e.target.value)} />
                        </div>
                        <div className="conn-form-group small">
                          <label>{t('connection:field.poolMax')}</label>
                          <input value={form.poolMax} onChange={e => handleFormChange('poolMax', e.target.value)} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* 고급 옵션 섹션 */}
                <div className="conn-advanced-toggle" onClick={() => toggleSection('advanced')}>
                  <span>{sections.advanced ? '▼' : '▶'}</span>
                  <span>{t('connection:field.advancedTitle')}</span>
                </div>

                {sections.advanced && (
                  <div className="conn-advanced-section">
                    {/* Oracle 드라이버 모드 (보통 변경 불필요 — 번들 Instant Client 자동 Thick) */}
                    {form.dbType === 'oracle' && (
                      <>
                        <div className="conn-advanced-subtitle">{t('connection:field.driverMode')}</div>
                        <div className="conn-type-group" style={{ marginBottom: 8 }}>
                          <button
                            type="button"
                            className={`conn-type-btn ${form.driverMode === 'thin' ? 'active' : ''}`}
                            style={form.driverMode === 'thin' ? { borderColor: '#007bff', color: '#007bff' } : {}}
                            onClick={() => handleFormChange('driverMode', 'thin')}
                            title={t('connection:driver.thinDesc')}
                          >
                            {t('connection:field.thinFallback')}
                          </button>
                          <button
                            type="button"
                            className={`conn-type-btn ${form.driverMode === 'thick' ? 'active' : ''}`}
                            style={form.driverMode === 'thick' ? { borderColor: '#6f42c1', color: '#6f42c1' } : {}}
                            onClick={() => handleFormChange('driverMode', 'thick')}
                            title={t('connection:driver.thickDesc')}
                          >
                            Thick (Oracle Client)
                          </button>
                        </div>
                        <div style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>
                          {t('connection:note.bundledThick')}
                          {t('connection:note.thinFallback')}
                        </div>
                      </>
                    )}
                    {/* SSH 터널 */}
                    <div className="conn-advanced-subtitle">{t('connection:field.sshTitle')}</div>
                    <div className="conn-toggle-row">
                      <input
                        type="checkbox"
                        id="sshEnable"
                        checked={form.sshEnable}
                        onChange={e => handleFormChange('sshEnable', e.target.checked)}
                      />
                      <label htmlFor="sshEnable">{t('connection:field.sshEnable')}</label>
                    </div>
                    {form.sshEnable && (
                      <>
                        <div className="conn-form-row">
                          <div className="conn-form-group" style={{ flex: 2 }}>
                            <label>{t('connection:field.sshHost')}</label>
                            <input value={form.sshHost} onChange={e => handleFormChange('sshHost', e.target.value)} placeholder="bastion.example.com" />
                          </div>
                          <div className="conn-form-group small">
                            <label>{t('connection:field.sshPort')}</label>
                            <input value={form.sshPort} onChange={e => handleFormChange('sshPort', e.target.value)} placeholder="22" />
                          </div>
                        </div>
                        <div className="conn-form-group">
                          <label>{t('connection:field.sshUser')}</label>
                          <input value={form.sshUser} onChange={e => handleFormChange('sshUser', e.target.value)} placeholder="deploy" />
                        </div>
                        <div className="conn-form-group">
                          <label>{t('connection:field.sshAuth')}</label>
                          <div className="conn-type-group">
                            <button type="button"
                              className={`conn-type-btn ${form.sshAuth === 'password' ? 'active' : ''}`}
                              style={form.sshAuth === 'password' ? { borderColor: '#007bff', color: '#007bff' } : {}}
                              onClick={() => handleFormChange('sshAuth', 'password')}>{t('connection:field.password')}</button>
                            <button type="button"
                              className={`conn-type-btn ${form.sshAuth === 'privateKey' ? 'active' : ''}`}
                              style={form.sshAuth === 'privateKey' ? { borderColor: '#6f42c1', color: '#6f42c1' } : {}}
                              onClick={() => handleFormChange('sshAuth', 'privateKey')}>{t('connection:field.sshPrivateKey')}</button>
                          </div>
                        </div>
                        {form.sshAuth === 'password' ? (
                          <div className="conn-form-group">
                            <label>{t('connection:field.sshPassword')}</label>
                            <input type="password" value={form.sshPassword} onChange={e => handleFormChange('sshPassword', e.target.value)} />
                          </div>
                        ) : (
                          <>
                            <div className="conn-form-group">
                              <label>{t('connection:field.sshPrivateKeyPem')}</label>
                              <textarea
                                value={form.sshPrivateKey}
                                onChange={e => handleFormChange('sshPrivateKey', e.target.value)}
                                placeholder={'-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----'}
                                rows={4}
                                style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 5, fontFamily: 'monospace', fontSize: 11 }}
                              />
                            </div>
                            <div className="conn-form-group">
                              <label>{t('connection:field.sshPassphrase')}</label>
                              <input type="password" value={form.sshPassphrase} onChange={e => handleFormChange('sshPassphrase', e.target.value)} />
                            </div>
                          </>
                        )}
                        <div style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>
                          {t('connection:note.sshTunnel')}
                        </div>
                      </>
                    )}

                    {/* 드라이버 속성 (key-value) */}
                    <div className="conn-advanced-subtitle">{t('connection:field.driverProps')}</div>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>
                      {t('connection:note.driverProps')}
                    </div>
                    {Object.entries(form.driverProps || {}).map(([k, v], idx) => (
                      <div key={`dp-${idx}`} className="conn-form-row" style={{ marginBottom: 4 }}>
                        <div className="conn-form-group" style={{ flex: 1 }}>
                          <input
                            value={k}
                            placeholder="key"
                            onChange={e => {
                              const newProps = { ...form.driverProps };
                              const oldKey = k;
                              const newKey = e.target.value;
                              delete newProps[oldKey];
                              newProps[newKey] = v;
                              handleFormChange('driverProps', newProps);
                            }}
                          />
                        </div>
                        <div className="conn-form-group" style={{ flex: 2 }}>
                          <input
                            value={v}
                            placeholder="value"
                            onChange={e => handleFormChange('driverProps', { ...form.driverProps, [k]: e.target.value })}
                          />
                        </div>
                        <button
                          className="app-btn app-btn-danger"
                          style={{ flex: '0 0 auto', padding: '4px 10px', fontSize: 12 }}
                          onClick={() => {
                            const newProps = { ...form.driverProps };
                            delete newProps[k];
                            handleFormChange('driverProps', newProps);
                          }}
                        >{t('connection:field.delete')}</button>
                      </div>
                    ))}
                    <button
                      className="app-btn"
                      style={{ padding: '4px 12px', fontSize: 12, marginBottom: 12 }}
                      onClick={() => handleFormChange('driverProps', { ...form.driverProps, '': '' })}
                    >{t('connection:field.addProp')}</button>

                    {/* D4: Shell commands (선택, 보안 주의) */}
                    <div className="conn-advanced-subtitle">Shell commands <span style={{ fontSize: 10, color: '#dc3545', fontWeight: 400, display: 'inline-flex', alignItems: 'center', gap: 2 }}>(<FiAlertTriangle size={10} /> {t('connection:field.shellWarning')}</span></div>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>
                      {t('connection:note.shell')}
                    </div>
                    <div className="conn-form-group">
                      <label>{t('connection:field.shellBefore')}</label>
                      <input value={form.shellBefore} onChange={e => handleFormChange('shellBefore', e.target.value)} placeholder={t('connection:form.shellPingPlaceholder')} />
                    </div>
                    <div className="conn-form-group">
                      <label>{t('connection:field.shellAfter')}</label>
                      <input value={form.shellAfter} onChange={e => handleFormChange('shellAfter', e.target.value)} placeholder={t('connection:form.shellCurlPlaceholder')} />
                    </div>
                    <div className="conn-form-group">
                      <label>{t('connection:field.shellDisconnect')}</label>
                      <input value={form.shellDisconnect} onChange={e => handleFormChange('shellDisconnect', e.target.value)} placeholder={t('connection:form.shellEchoPlaceholder')} />
                    </div>

                    {/* Session boot SQL */}
                    <div className="conn-advanced-subtitle">{t('connection:field.bootSql')}</div>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>
                      {t('connection:note.bootSqlPre')} <code>ALTER SESSION SET NLS_DATE_FORMAT = 'YYYY-MM-DD HH24:MI:SS'</code>
                    </div>
                    <div className="conn-form-group">
                      <textarea
                        value={(form.bootStatements || []).join('\n')}
                        onChange={e => {
                          const lines = e.target.value.split('\n').filter(l => l !== undefined);
                          handleFormChange('bootStatements', lines);
                        }}
                        rows={4}
                        placeholder={'ALTER SESSION SET NLS_DATE_FORMAT = \'YYYY-MM-DD HH24:MI:SS\'\nALTER SESSION SET CURRENT_SCHEMA = MYSCHEMA'}
                        style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 5, fontFamily: 'monospace', fontSize: 12 }}
                      />
                    </div>

                    {/* 커넥션 스트링 직접 입력 */}
                    <div className="conn-advanced-subtitle">{t('connection:field.directConnectString')}</div>
                    <div className="conn-toggle-row">
                      <input
                        type="checkbox"
                        id="useDirectConnectString"
                        checked={form.useDirectConnectString}
                        onChange={e => handleFormChange('useDirectConnectString', e.target.checked)}
                      />
                      <label htmlFor="useDirectConnectString">{t('connection:field.directModeHint')}</label>
                    </div>
                    <div className="conn-form-group">
                      <textarea
                        disabled={!form.useDirectConnectString}
                        value={form.connectStringDirect}
                        onChange={e => handleFormChange('connectStringDirect', e.target.value)}
                        placeholder={t('connection:form.connectStringPlaceholder')}
                      />
                    </div>

                    {/* SSL/TLS */}
                    <div className="conn-advanced-subtitle">SSL/TLS</div>
                    <div className="conn-toggle-row">
                      <input
                        type="checkbox"
                        id="enableSsl"
                        checked={form.enableSsl}
                        onChange={e => handleFormChange('enableSsl', e.target.checked)}
                      />
                      <label htmlFor="enableSsl">{t('connection:field.sslEnable')}</label>
                    </div>
                    <div className="conn-toggle-row" style={{ opacity: form.enableSsl ? 1 : 0.5, pointerEvents: form.enableSsl ? 'auto' : 'none' }}>
                      <input
                        type="checkbox"
                        id="sslServerDNMatch"
                        checked={form.sslServerDNMatch}
                        onChange={e => handleFormChange('sslServerDNMatch', e.target.checked)}
                        disabled={!form.enableSsl}
                      />
                      <label htmlFor="sslServerDNMatch">{t('connection:field.sslDnMatch')}</label>
                    </div>
                    {/* D2: CA / Client cert (PEM) */}
                    {form.enableSsl && (
                      <>
                        <div className="conn-form-group">
                          <label>{t('connection:field.sslCaCert')}</label>
                          <textarea
                            value={form.sslCaCert}
                            onChange={e => handleFormChange('sslCaCert', e.target.value)}
                            rows={3}
                            placeholder={'-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'}
                            style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 5, fontFamily: 'monospace', fontSize: 11 }}
                          />
                        </div>
                        <div className="conn-form-group">
                          <label>{t('connection:field.sslClientCert')}</label>
                          <textarea
                            value={form.sslClientCert}
                            onChange={e => handleFormChange('sslClientCert', e.target.value)}
                            rows={3}
                            placeholder={'-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'}
                            style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 5, fontFamily: 'monospace', fontSize: 11 }}
                          />
                        </div>
                        <div className="conn-form-group">
                          <label>{t('connection:field.sslClientKey')}</label>
                          <textarea
                            value={form.sslClientKey}
                            onChange={e => handleFormChange('sslClientKey', e.target.value)}
                            rows={3}
                            placeholder={'-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----'}
                            style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 5, fontFamily: 'monospace', fontSize: 11 }}
                          />
                        </div>
                        <div style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>
                          {t('connection:note.sslPerDb')}
                        </div>
                      </>
                    )}
                    <div className="conn-form-group">
                      <label>{t('connection:field.expireTimeout')}</label>
                      <input
                        value={form.expireTime}
                        onChange={e => handleFormChange('expireTime', e.target.value)}
                        placeholder={t('connection:form.expirePlaceholder')}
                        type="number"
                      />
                    </div>

                    {/* HTTPS 프록시 */}
                    <div className="conn-advanced-subtitle">{t('connection:field.proxyTitle')}</div>
                    <div className="conn-form-row">
                      <div className="conn-form-group">
                        <label>{t('connection:field.proxyHost')}</label>
                        <input
                          value={form.httpsProxyHost}
                          onChange={e => handleFormChange('httpsProxyHost', e.target.value)}
                          placeholder={t('connection:form.proxyPlaceholder')}
                        />
                      </div>
                      <div className="conn-form-group small">
                        <label>{t('connection:field.port')}</label>
                        <input
                          value={form.httpsProxyPort}
                          onChange={e => handleFormChange('httpsProxyPort', e.target.value)}
                          placeholder="3128"
                          type="number"
                        />
                      </div>
                    </div>

                    {/* 타임아웃 */}
                    <div className="conn-advanced-subtitle">{t('connection:field.timeoutTitle')}</div>
                    <div className="conn-form-group">
                      <label>{t('connection:field.connectTimeout')}</label>
                      <input
                        value={form.connectTimeout}
                        onChange={e => handleFormChange('connectTimeout', e.target.value)}
                        placeholder={t('connection:form.connectTimeoutPlaceholder')}
                        type="number"
                      />
                    </div>
                    <div className="conn-form-group">
                      <label>{t('connection:field.queueTimeout')}</label>
                      <input
                        value={form.queueTimeout}
                        onChange={e => handleFormChange('queueTimeout', e.target.value)}
                        placeholder={t('connection:form.queueTimeoutPlaceholder')}
                        type="number"
                      />
                    </div>
                  </div>
                )}

                {testResult && (
                  <div className={`conn-test-result ${testResult.success ? 'success' : 'error'}`}>
                    {testResult.message}
                    {testResult.dbVersion && <div style={{ marginTop: 4 }}>{testResult.dbVersion}</div>}
                  </div>
                )}
              </div>

              <div className="conn-detail-footer app-modal-footer">
                <button className="app-btn" onClick={handleTest} disabled={!isFormValid || isTesting}>
                  {isTesting ? t('connection:action.testing') : t('connection:action.test')}
                </button>
                {!(selectedConn && (selectedConn.sharedFrom || (selectedConn.org && selectedConn.org.viaOrg))) && (
                  <button className="app-btn app-btn-primary" onClick={handleSave} disabled={!isFormValid} title={dirty ? t('connection:save.unsavedBadge') : undefined}>
                    {t('connection:act.save')}{dirty ? ' ●' : ''}
                  </button>
                )}
                {!isNew && !(selectedConn && (selectedConn.sharedFrom || (selectedConn.org && selectedConn.org.viaOrg))) && (
                  <button className="app-btn app-btn-danger" onClick={handleDelete}>{t('connection:field.delete')}</button>
                )}
                {/* 조직에 공개 — 내가 만든 연결을, 내가 관리자인 조직의 구성원 전원에게. 기본 읽기 전용(주인의 DB 계정이다) */}
                {!isNew && selectedConn && selectedConn.isMine && !IS_STANDALONE && adminOrgs.length > 0 && (
                  <label className="conn-org-pick" title={t('common:org.connPublishHint')}>
                    <FiUsers size={12} />
                    <select
                      value={selectedConn.org ? String(selectedConn.org.id) : ''}
                      onChange={async (e) => {
                        const orgId = e.target.value ? Number(e.target.value) : null;
                        try { await orgs.setConnectionOrg(selectedConn.id, orgId, !!(selectedConn.org && selectedConn.org.write)); await loadConnections(); }
                        catch (err) { toast.error(formatApiError(err, t)); }
                      }}
                    >
                      <option value="">{t('common:org.connNotPublished')}</option>
                      {adminOrgs.map(o => <option key={o.id} value={String(o.id)}>{t('common:org.connPublishTo', { name: o.name })}</option>)}
                      {selectedConn.org && !adminOrgs.some(o => o.id === selectedConn.org.id) && <option value={String(selectedConn.org.id)}>{selectedConn.org.name}</option>}
                    </select>
                    {selectedConn.org && (
                      <span className="conn-org-write">
                        <input type="checkbox" checked={!!selectedConn.org.write} onChange={async (e) => {
                          try { await orgs.setConnectionOrg(selectedConn.id, selectedConn.org.id, e.target.checked); await loadConnections(); }
                          catch (err) { toast.error(formatApiError(err, t)); }
                        }} />
                        {t('common:org.allowWrite')}
                      </span>
                    )}
                  </label>
                )}
                {selectedConn && selectedConn.org && selectedConn.org.viaOrg && (
                  <span className="conn-org-note"><FiUsers size={12} /> {t(selectedConn.org.write ? 'common:org.viaOrgRw' : 'common:org.viaOrgRo', { name: selectedConn.org.name })}</span>
                )}
                {/* 공유 — 내가 만든 개인 연결만(공용 연결은 이미 모두의 것, 받은 연결은 내 것이 아니다) */}
                {!isNew && selectedConn && selectedConn.isMine && !IS_STANDALONE && (
                  <button className="app-btn" onClick={() => { setShareTargetId(selectedConn.id); setActiveTab('sharing'); }} title={t('common:share.connHint')}>
                    <FiShare2 size={12} /> {t('common:share.share')}
                  </button>
                )}
                {selectedConn && selectedConn.sharedFrom && (
                  <button className="app-btn" onClick={async () => {
                    const ok = await confirm({ title: t('common:share.leaveTitle'), message: t('common:share.leaveConn', { name: selectedConn.name }), confirmText: t('common:share.leave'), variant: 'danger' });
                    if (!ok) return;
                    try { await shareApi.leaveConn(selectedConn.id); setSelectedId(null); await loadConnections(); setIncomingKey(k => k + 1); } catch (err) { toast.error(formatApiError(err, t)); }
                  }}>{t('common:share.leave')}</button>
                )}
                <div className="app-modal-footer-spacer" />
                {!isNew && (
                  <button className="app-btn app-btn-primary" onClick={handleConnect} disabled={!isFormValid || isConnecting}>
                    {isConnecting ? t('connection:action.connecting') : t('connection:action.connect')}
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="conn-empty">
              {t('connection:field.pickOrAdd')}
            </div>
          )}
        </div>
        </div>
        )}
        <input
          ref={importFileRef}
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleImportFile}
        />
    </AppModal>
  );
};

export default ConnectionModal;
